<template>
  <div class="tw-w-full tw-h-full tw-bg-gray-50x tw-min-h-[600px] tw-p-4x tw-space-y-4">
    <!-- Congratulations Banner -->
    <div 
      v-if="!bannerDismissed"
      class="tw-bg-primary-500 tw-text-white tw-rounded-lg tw-p-4 tw-flex tw-items-center tw-justify-between tw-shadow-sm"
    >
      <div class="tw-flex tw-items-center tw-gap-3">
        <div class="tw-bg-white tw-bg-opacity-20 tw-rounded-full tw-p-2">
          <icon icon="solar:medal-ribbons-star-bold" class="tw-text-white tw-text-xl" />
        </div>
        <div>
          <h3 class="tw-font-bold tw-text-lg" >Congratulations! {{ $company.name }}</h3>
          <p class="tw-text-sm tw-text-primary-100">Your Company is among the 1% most searched for companies</p>
        </div>
      </div>
      <button 
        @click="dismissBanner"
        class="tw-text-white tw-hover:tw-bg-white tw-hover:tw-bg-opacity-20 tw-rounded-full tw-p-2 tw-transition-colors"
        aria-label="Dismiss notification"
      >
        <icon icon="solar:close-line-linear" class="tw-text-lg" />
      </button>
    </div>

    <!-- Welcome Section -->
    <div class="tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-primary-200 tw-shadow-sm">
      <h1 class="tw-text-3xl tw-font-bold tw-text-primary-950 tw-mb-4">Welcome to the Company Dashboard</h1>
      
      <p class="tw-text-gray-600 tw-mb-6 tw-leading-relaxed">
        This portal allows you to increase your company's visibility, control the content people will receive about you, and 
        maximize your ROI.
      </p>

      <div class="tw-mb-4">
        <h2 class="tw-text-lg tw-font-semibold tw-text-primary-950 tw-mb-3">You are able to:</h2>
        <ul class="tw-space-y-2 tw-text-gray-700">
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Track your company's KPIs</span>
          </li>
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Manage and update your company profile</span>
          </li>
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Gather all new contacts of your booth managers and export them in a single Excel file</span>
          </li>
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Promote your products or services to potential leads</span>
          </li>
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Reply to meeting requests made to your company, and manage meetings of your members</span>
          </li>
          <li class="tw-flex tw-items-start tw-gap-2">
            <icon icon="solar:check-circle-bold" class="tw-text-primary-500 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
            <span>Keep track of your team's leads and success at The FoodEshow</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Analytics Section -->
    <div class="tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-primary-200 tw-shadow-sm">
      <h2 class="tw-text-2xl tw-font-bold tw-text-primary-950 tw-mb-6">Your company analytics</h2>
      
      <!-- Analytics Grid -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
        <div 
          v-for="metric in analyticsData" 
          :key="metric.label"
          class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-200"
        >
          <div class="tw-flex tw-items-start tw-justify-between tw-mb-2">
            <div class="tw-text-3xl tw-font-bold tw-text-gray-800">{{ metric.value }}</div>
            <div class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8">
              <icon 
                :icon="getTrendIcon(metric.trend)" 
                :class="getTrendColor(metric.trend)"
                class="tw-text-lg"
              />
            </div>
          </div>
          <div class="tw-text-sm tw-text-gray-600 tw-font-medium">{{ metric.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

// Banner state
const bannerDismissed = ref(false)

// Analytics demo data
const analyticsData = ref([
  { value: '500', label: 'profile clicks', trend: 'up' },
  { value: '1K', label: 'profile views', trend: 'down' },
  { value: '200', label: 'Product clicks', trend: 'up' },
  { value: '300', label: 'Service clicks', trend: 'neutral' },
  { value: '1500', label: 'contacts', trend: 'up' },
  { value: '38 mins', label: 'average meeting duration', trend: 'down' },
  { value: '25', label: 'ongoing conversations', trend: 'up' },
  { value: '5', label: 'sessions attended', trend: 'up' }
])

// Methods
const dismissBanner = () => {
  bannerDismissed.value = true
}

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'up':
      return 'solar:arrow-up-linear'
    case 'down':
      return 'solar:arrow-down-linear'
    case 'neutral':
    default:
      return 'solar:minus-linear'
  }
}

const getTrendColor = (trend) => {
  switch (trend) {
    case 'up':
      return 'tw-text-primary-500'
    case 'down':
      return 'tw-text-red-500'
    case 'neutral':
    default:
      return 'tw-text-gray-400'
  }
}
</script>

<style scoped>
</style>